import { common, green } from '@mui/material/colors'

import { MARKER } from 'theme/colors'

/**
 * Canvas pill-marker generator — direct port of the DOM StopMarker design.
 *
 * Layer stack (outside → inside), matching DOM chip exactly:
 *   ┌─────────────────────┐  ← outer pill   (outerColor / route ring)
 *   │  ┌───────────────┐  │  ← white ring   (inset box-shadow 1.5px #fff)
 *   │  │  ┌─────────┐  │  │  ← inner pill   (innerColor / status fill)
 *   │  │  │   125   │  │  │  ← sequence label
 *   │  │  └─────────┘  │  │
 *   │  └───────────────┘  │
 *   └──────────▼──────────┘  ← triangle tip (outerColor), anchor point for deck.gl
 *
 * deck.gl IconLayer anchor:
 *   anchorX: 0.5  (horizontal centre)
 *   anchorY: 1.0  (bottom edge = tip of triangle)
 *
 * Returns { canvas, width, height } where width/height are in CSS pixels.
 * Call canvas.toDataURL() to get an image URL for IconLayer.
 */

const SCALE = 1
// Render canvas at 2× to oversample — deck.gl packs icons into a texture atlas and the
// default 1× canvas at DPR is re-sampled when drawn to the atlas, causing blurriness.
// 2× oversample means the GPU has 4× more pixels to work with, yielding much sharper text.
const OVERSAMPLE = 2

// Thin by default; a selected marker doubles it so the highlight reads without moving the pin.
const OUTLINE_W = 1.25 * SCALE

// The renderer paints to a canvas, so these are concrete colours rather than theme tokens.
const DEFAULT_INNER = green[500]
const DEFAULT_OUTER = MARKER.defaultPillRing

export const PILL_CSS_HEIGHT = (16 + 8 + 7 + 4) * SCALE // 35

// Module-level 2D context used solely for text measurement.
// Avoids allocating a throwaway canvas element on every unique icon generation.
// eslint-disable-next-line no-underscore-dangle -- module-private name; intentional `_` prefix to mark it as such
const _measureCtx = (() => {
  const c = document.createElement('canvas')
  c.width = 1
  c.height = 1
  return c.getContext('2d')
})()

function drawRoundedRect(ctx, x, y, w, h, r, fill, stroke, lw) {
  ctx.beginPath()
  ctx.roundRect(x, y, w, h, r)
  ctx.fillStyle = fill
  ctx.fill()
  if (lw > 0) {
    ctx.lineWidth = lw
    ctx.strokeStyle = stroke
    ctx.stroke()
  }
}

export function generatePillIcon(
  sequence,
  innerColor = DEFAULT_INNER,
  outerColor = DEFAULT_OUTER,
  textColor = common.white,
  strokeColor = null,
  strokeWidth = OUTLINE_W
) {
  const dpi = (typeof window !== 'undefined' ? window.devicePixelRatio || 2 : 2) * OVERSAMPLE

  // Match DOM StopMarker (MUI Chip) exactly:
  //   font-size = 0.8125rem = 13px, fontWeight = 900
  //   Chip height = 24px border-box with 4px border → inner content = 16px
  //   Chip minWidth = 2.5rem = 40px, label padding = 6px + 6px
  //   border: 6px solid outerColor  (thicker than DOM's 3px for more prominence)
  //   inset box-shadow: 0 0 0 1.5px white  (white ring between border and fill)
  //   Triangle tip = 7px (borderTop: 7px in DOM)
  const fontSize = 13 * SCALE
  const innerH = 16 * SCALE // content height
  const minInnerW = 32 * SCALE // 40px outer - 4px each side = 32px inner min
  const paddingX = 12 * SCALE // 6px left + 6px right label padding
  const outerPadding = 8 * SCALE // 4px border each side
  const whiteRingW = 1 * SCALE // thin white separator ring
  const tipW = 14 * SCALE // DOM borderLeft/Right: 7px each = 14px wide
  const tipH = 7 * SCALE // DOM borderTop: 7px
  const radius = 100 * SCALE // Large enough radius = fully-rounded pill

  // Measure text using the shared module-level context — no throwaway canvas needed.
  _measureCtx.font = `900 ${fontSize}px "Inter", sans-serif`
  const textW = _measureCtx.measureText(String(sequence)).width
  const innerW = Math.max(minInnerW, textW + paddingX)
  const outerW = innerW + outerPadding
  const outerH = innerH + outerPadding

  // Buffer so neither the outline nor the tip is clipped; the outline grows the marker on every side.
  const outline = strokeColor ? strokeWidth : 0
  const totalW = outerW + 4 * SCALE + 2 * outline
  const totalH = outerH + tipH + 4 * SCALE + 2 * outline

  // Create the canvas at the correct size from the start — no resize needed.
  const canvas = document.createElement('canvas')
  canvas.width = Math.ceil(totalW * dpi)
  canvas.height = Math.ceil(totalH * dpi)
  const ctx = canvas.getContext('2d')
  ctx.scale(dpi, dpi)

  // Horizontal centre; slight downward shift so the top border isn't clipped
  const cx = totalW / 2
  const cy = outerH / 2 + SCALE + outline

  // A silhouette behind the marker, not a centred stroke, which would thicken where the tip meets the pill.
  if (outline) {
    const tipTop = cy + outerH / 2 - 1.5 * SCALE
    drawRoundedRect(
      ctx,
      cx - outerW / 2 - outline,
      cy - outerH / 2 - outline,
      outerW + 2 * outline,
      outerH + 2 * outline,
      radius,
      strokeColor,
      strokeColor,
      0
    )
    ctx.beginPath()
    ctx.moveTo(cx - tipW / 2 - outline, tipTop)
    ctx.lineTo(cx + tipW / 2 + outline, tipTop)
    ctx.lineTo(cx, cy + outerH / 2 + tipH + outline * 1.8)
    ctx.closePath()
    ctx.fillStyle = strokeColor
    ctx.fill()
  }

  // 1. Outer pill — solid route color (the coloured ring)
  drawRoundedRect(ctx, cx - outerW / 2, cy - outerH / 2, outerW, outerH, radius, outerColor, outerColor, 0)

  // 2. White ring — thin halo just outside the inner pill.
  //    Must be sized as innerPill + 2×whiteRingW, NOT outerPill - 2×whiteRingW.
  //    Using outerPill-based sizing floods the whole gap between ring and fill with white.
  const wrW = innerW + 2 * whiteRingW
  const wrH = innerH + 2 * whiteRingW
  drawRoundedRect(ctx, cx - wrW / 2, cy - wrH / 2, wrW, wrH, radius, common.white, common.white, 0)

  // 3. Inner pill — status color fill
  drawRoundedRect(ctx, cx - innerW / 2, cy - innerH / 2, innerW, innerH, radius, innerColor, innerColor, 0)

  // 4. Sequence label — re-apply font after canvas resize
  ctx.font = `900 ${fontSize}px "Inter", sans-serif`
  ctx.fillStyle = textColor
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(String(sequence), cx, cy + SCALE)

  // 5. Triangle tip pointing down (outerColor — matches outer ring)
  ctx.beginPath()
  ctx.moveTo(cx - tipW / 2, cy + outerH / 2 - 1.5 * SCALE)
  ctx.lineTo(cx + tipW / 2, cy + outerH / 2 - 1.5 * SCALE)
  ctx.lineTo(cx, cy + outerH / 2 + tipH)
  ctx.closePath()
  ctx.fillStyle = outerColor
  ctx.fill()

  return { canvas, width: totalW, height: totalH }
}

/** Convenience: returns a data URL directly. */
export function generatePillIconUrl(sequence, innerColor, outerColor, textColor, strokeColor, strokeWidth) {
  return generatePillIcon(sequence, innerColor, outerColor, textColor, strokeColor, strokeWidth).canvas.toDataURL()
}

// ---------------------------------------------------------------------------
// Shell variant — same pill shape but WITHOUT sequence text.
// One shell per (innerColor, outerColor) pair covers ALL stops on a route,
// so the atlas has O(routes) entries instead of O(stops).
// Sequence numbers are rendered by a companion TextLayer in DeckMarkerRenderer.
// ---------------------------------------------------------------------------

// Default inner width that fits 1–3 digit sequence numbers with comfortable padding.
const PILL_SHELL_INNER_W = 32 * SCALE

/**
 * Returns the pill inner width based on total stop count.
 * Wider pills for 4+ digit labels so text doesn't overflow the shell.
 */
export function getPillInnerWidth(totalStopCount = 0) {
  if (totalStopCount >= 10000) return 48 * SCALE
  if (totalStopCount >= 1000) return 40 * SCALE
  return PILL_SHELL_INNER_W
}

// Pixel offset (screen pixels) from the bottom-tip anchor down to the centre of
// the pill body.  Used by the companion TextLayer so text aligns with the icon.
//
//   outerH  = innerH + outerPadding = 16 + 8 = 24
//   tipH    = 7,  buffer = 4
//   totalH  = outerH + tipH + buffer = 35
//   cy (text centre from top) = outerH / 2 + SCALE = 13
//   offset  = -(totalH - cy) = -(35 - 13) = -22
export const PILL_TEXT_PIXEL_OFFSET_Y = -22

/**
 * @param {string} innerColor - Inner pill fill (status / bg colour).
 * @param {string} outerColor - Outer ring + tip colour (route / border colour).
 * @param {number} [innerWidth] - Override inner pill width (CSS pixels). Defaults to PILL_SHELL_INNER_W.
 * @returns {{ canvas: HTMLCanvasElement, width: number, height: number }}
 */
export function generatePillShell(innerColor = DEFAULT_INNER, outerColor = DEFAULT_OUTER, innerWidth, outlineColor) {
  const dpi = (typeof window !== 'undefined' ? window.devicePixelRatio || 2 : 2) * OVERSAMPLE

  const innerH = 16 * SCALE
  const outerPadding = 8 * SCALE
  const whiteRingW = 1 * SCALE
  const outlineW = outlineColor ? 3 * SCALE : 0
  const tipW = 14 * SCALE
  const tipH = 7 * SCALE
  const radius = 100 * SCALE

  const innerW = innerWidth || PILL_SHELL_INNER_W
  const outerW = innerW + outerPadding
  const outerH = innerH + outerPadding

  const totalW = outerW + 2 * outlineW + 4 * SCALE
  const totalH = outerH + tipH + 2 * outlineW + 4 * SCALE

  const canvas = document.createElement('canvas')
  canvas.width = Math.ceil(totalW * dpi)
  canvas.height = Math.ceil(totalH * dpi)

  const ctx = canvas.getContext('2d')
  ctx.scale(dpi, dpi)

  const cx = totalW / 2
  const cy = outerH / 2 + SCALE + outlineW

  if (outlineW > 0) {
    const haloW = outerW + 2 * outlineW
    const haloH = outerH + 2 * outlineW
    drawRoundedRect(ctx, cx - haloW / 2, cy - haloH / 2, haloW, haloH, radius, outlineColor, outlineColor, 0)
  }

  drawRoundedRect(ctx, cx - outerW / 2, cy - outerH / 2, outerW, outerH, radius, outerColor, outerColor, 0)

  const wrW = innerW + 2 * whiteRingW
  const wrH = innerH + 2 * whiteRingW
  drawRoundedRect(ctx, cx - wrW / 2, cy - wrH / 2, wrW, wrH, radius, common.white, common.white, 0)

  drawRoundedRect(ctx, cx - innerW / 2, cy - innerH / 2, innerW, innerH, radius, innerColor, innerColor, 0)

  const tipBaseY = cy + outerH / 2 - 1.5 * SCALE
  if (outlineW > 0) {
    const haloTipW = tipW + 2 * outlineW
    const haloTipH = tipH + outlineW
    ctx.beginPath()
    ctx.moveTo(cx - haloTipW / 2, tipBaseY)
    ctx.lineTo(cx + haloTipW / 2, tipBaseY)
    ctx.lineTo(cx, tipBaseY + haloTipH)
    ctx.closePath()
    ctx.fillStyle = outlineColor
    ctx.fill()
  }
  ctx.beginPath()
  ctx.moveTo(cx - tipW / 2, tipBaseY)
  ctx.lineTo(cx + tipW / 2, tipBaseY)
  ctx.lineTo(cx, tipBaseY + tipH)
  ctx.closePath()
  ctx.fillStyle = outerColor
  ctx.fill()

  return { canvas, width: totalW, height: totalH }
}

// ---------------------------------------------------------------------------
// Halo variant — a stroked ring the SHAPE of the pill body (rounded-rect), not
// a circle. Sized to sit OUTSIDE the pill with a gap so a selected stop reads as
// a detached pill-shaped outline. Text-free and colour-only, so one canvas per
// (haloColor, innerWidth) covers every selected stop on a route.
//
// Anchored at the ring centre — callers offset it to the pill body centre with
// PILL_TEXT_PIXEL_OFFSET_Y, exactly like the companion TextLayer.
// ---------------------------------------------------------------------------

/**
 * @param {string} haloColor    - Ring stroke colour.
 * @param {number} [innerWidth] - Pill inner width (CSS px); match the shell's.
 * @param {number} [gap]        - Daylight between the pill edge and the ring (CSS px).
 * @param {number} [lineWidth]  - Ring stroke width (CSS px).
 * @returns {{ canvas: HTMLCanvasElement, width: number, height: number }}
 */
export function generatePillHalo(haloColor, innerWidth, gap = 3, lineWidth = 2) {
  const dpi = (typeof window !== 'undefined' ? window.devicePixelRatio || 2 : 2) * OVERSAMPLE

  const innerH = 16 * SCALE
  const outerPadding = 8 * SCALE
  const radius = 100 * SCALE

  const innerW = innerWidth || PILL_SHELL_INNER_W
  const outerW = innerW + outerPadding
  const outerH = innerH + outerPadding

  // Ring rect = pill body expanded by the gap on every side; stroke centred on it.
  const ringW = outerW + 2 * gap
  const ringH = outerH + 2 * gap
  const buffer = lineWidth + 2 * SCALE
  const totalW = ringW + 2 * buffer
  const totalH = ringH + 2 * buffer

  const canvas = document.createElement('canvas')
  canvas.width = Math.ceil(totalW * dpi)
  canvas.height = Math.ceil(totalH * dpi)
  const ctx = canvas.getContext('2d')
  ctx.scale(dpi, dpi)

  const cx = totalW / 2
  const cy = totalH / 2
  ctx.beginPath()
  ctx.roundRect(cx - ringW / 2, cy - ringH / 2, ringW, ringH, radius)
  ctx.lineWidth = lineWidth
  ctx.strokeStyle = haloColor
  ctx.stroke()

  return { canvas, width: totalW, height: totalH }
}
